"use strict";

/**
 * @namespace
 */
gc.gis.log = gc.gis.log || {};

/**
 * Log-Levels.
 *
 * @enum {string}
 */
gc.gis.log.GCLogLevel = {
	DEBUG:   'debug',
	INFO:    'info',
	WARNING: 'warning',
	ERROR:   'error'
};

/**
 * Log-Event-Typen.
 *
 * @enum {string}
 */
gc.gis.log.GCLogEvent = {
	/**
	 * Beim Leeren der Log-History.
	 * @event gc.gis.log.GCLog#log:clear
	 */
	CLEAR:   'log:clear',
	/**
	 * Bei jeder Art von Log-Event.
	 * @event gc.gis.log.GCLog#log:message
	 */
	MESSAGE: 'log:message',
	/**
	 * Bei Fehlern.
	 * @event gc.gis.log.GCLog#log:error
	 */
	ERROR:   'log:error',
	/**
	 * Bei Warnungen.
	 * @event gc.gis.log.GCLog#log:warning
	 */
	WARNING: 'log:warning',
	/**
	 * Bei Info-Meldungen.
	 * @event gc.gis.log.GCLog#log:info
	 */
	INFO:    'log:info',
	/**
	 * Bei Debug-Meldungen.
	 * @event gc.gis.log.GCLog#log:debug
	 */
	DEBUG:   'log:debug'
};

/**
 * @classdesc
 * Logger-Klasse.
 *
 * @class
 * @extends gc.gis.GCObservable
 * @memberof gc.gis.log
 * @author Anton Popov <a.popov@brain-scc.de>
 */
gc.gis.log.GCLog = function()
{
	gc.gis.GCObservable.call( this );

	/**
	 * @private
	 * @type {Object<string,Object[]>}
	 */
	this.__logs = {
		error:   [],
		warning: [],
		info:    [],
		debug:   []
	};
};
gc.gis.log.GCLog.prototype = Object.create( gc.gis.GCObservable.prototype );

/**
 * Gesamte Log-History löschen.
 *
 * @public
 */
gc.gis.log.GCLog.prototype.clear = function()
{
	delete this.__logs.error;
	delete this.__logs.warning;
	delete this.__logs.info;
	delete this.__logs.debug;

	this.__logs.error   = [];
	this.__logs.warning = [];
	this.__logs.info    = [];
	this.__logs.debug   = [];

	this._fireEvent( gc.gis.log.GCLogEvent.CLEAR, {} );
};

/**
 * Gibt die Anzahl an Log-Meldungen aller Log-GCLogLevel zusammen (ohne Parameter)
 * bzw. eines bestimmten Log-Levels der History zurück.
 *
 * @public
 * @param {gc.gis.log.GCLogLevel} [opt_level] Optionales Log-GCLogLevel
 * @return {number}
 */
gc.gis.log.GCLog.prototype.getMessageCount = function( opt_level )
{
	if( opt_level && this.__logs[opt_level] ) {
		return this.__logs[opt_level].length;
	}

	return this.__logs.error.length
		+ this.__logs.warning.length
		+ this.__logs.info.length
		+ this.__logs.debug.length;
};

/**
 * Gibt die Log-Meldungen aller Log-GCLogLevel (ohne Parameter) bzw. die eines
 * bestimmten Log-Levels der History zurück.
 *
 * @public
 * @param {gc.gis.log.GCLogLevel} [opt_level] Optionales Log-GCLogLevel
 * @return {Object[]} Log-Events
 */
gc.gis.log.GCLog.prototype.getMessages = function( opt_level )
{
	if( opt_level && this.__logs[opt_level] ) {
		return this.__logs[opt_level];
	}

	return this.__logs;
};

/**
 * Setzt ein Log-Event ab und reiht es in die History ein.
 * Feuert dabei immer zwei Events ab:
 * <ul>
 * <li>je nach Log-GCLogLevel:
 *   {@link gc.gis.log.GCLogEvent.ERROR},
 *   {@link gc.gis.log.GCLogEvent.WARNING},
 *   {@link gc.gis.log.GCLogEvent.INFO} oder
 *   {@link gc.gis.log.GCLogEvent.DEBUG}</li>
 * <li>in jedem Fall {@link gc.gis.log.GCLogEvent.MESSAGE}</li>
 * </ul>
 *
 * @private
 * @param {Object} context Kontext-Objekt der Meldung
 * @param {string|Error} message Meldungstext/Fehlerobjekt
 * @param {gc.gis.log.GCLogLevel} level Log-Level der Meldung
 * @param {boolean} [opt_notify=true] Benachrichtigung anzeigen?
 *
 * @fires gc.gis.log.GCLogEvent#log:message immer
 * @fires gc.gis.log.GCLogEvent#log:error   je nach Log-Level
 * @fires gc.gis.log.GCLogEvent#log:warning je nach Log-Level
 * @fires gc.gis.log.GCLogEvent#log:info    je nach Log-Level
 * @fires gc.gis.log.GCLogEvent#log:debug   je nach Log-Level
 */
gc.gis.log.GCLog.prototype.__logMessage = function( context, message, level, opt_notify )
{
	gc.gis.assert.isSet( context );
	gc.gis.assert.isSet( message );
	gc.gis.assert.isSet( level );

	var notify = opt_notify === undefined ||
		((typeof opt_notify === 'boolean') && opt_notify !== false);

	var log_event = {
		notify:  notify,
		context: context,
		message: message,
		level:   level,
		time:    Date.now()
	};

	switch( level )
	{
		case gc.gis.log.GCLogLevel.ERROR:
			this.__logs.error.push( log_event );
			this._fireEvent( gc.gis.log.GCLogEvent.ERROR, log_event );
			break;

		case gc.gis.log.GCLogLevel.WARNING:
			this.__logs.warning.push( log_event );
			this._fireEvent( gc.gis.log.GCLogEvent.WARNING, log_event );
			break;

		case gc.gis.log.GCLogLevel.INFO:
			this.__logs.info.push( log_event );
			this._fireEvent( gc.gis.log.GCLogEvent.INFO, log_event );
			break;

		case gc.gis.log.GCLogLevel.DEBUG:
			this.__logs.debug.push( log_event );
			this._fireEvent( gc.gis.log.GCLogEvent.DEBUG, log_event );
			break;

		default:
			throw new TypeError('Invalid log level: '+ level );	// sollte nicht passieren
	}

	this._fireEvent( gc.gis.log.GCLogEvent.MESSAGE, log_event );
};

/**
 * @public
 * @param {Object} context Kontext-Objekt der Meldung
 * @param {string|Error} message Meldungstext/Fehlerobjekt
 * @parem {boolean} [opt_notify=true] Benachrichtigung anzeigen?
 */
gc.gis.log.GCLog.prototype.error = function( context, message, opt_notify )
{
	this.__logMessage(context, message, gc.gis.log.GCLogLevel.ERROR, opt_notify);
};

/**
 * @public
 * @param {Object} context Kontext-Objekt der Meldung
 * @param {string} message Meldungstext
 * @parem {boolean} [opt_notify=true] Benachrichtigung anzeigen?
 */
gc.gis.log.GCLog.prototype.warn = function( context, message, opt_notify )
{
	this.__logMessage( context, message, gc.gis.log.GCLogLevel.WARNING, opt_notify );
};

/**
 * @public
 * @param {Object} context Kontext-Objekt der Meldung
 * @param {string} message Meldungstext
 * @parem {boolean} [opt_notify=true] Benachrichtigung anzeigen?
 */
gc.gis.log.GCLog.prototype.info = function( context, message, opt_notify )
{
	this.__logMessage( context, message, gc.gis.log.GCLogLevel.INFO, opt_notify );
};

/**
 * @public
 * @param {Object} context Kontext-Objekt der Meldung
 * @param {string} message Meldungstext
 * @parem {boolean} [opt_notify=true] Benachrichtigung anzeigen?
 */
gc.gis.log.GCLog.prototype.debug = function( context, message, opt_notify )
{
	this.__logMessage( context, message, gc.gis.log.GCLogLevel.DEBUG, opt_notify );
}; 

